import { merge } from 'icepick';
import { v4 as uuid } from 'uuid';
import { defaultLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { chainUnsafe as chain } from '@atlassian/jira-common-icepick/src/utils/unsafe/index.tsx';
import { defaultTimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import {
	INITIALIZE_APP_PROPS,
	SET_APP_DATA,
	SET_CONFIG_DATA,
	LOAD_CONFIG_DATA_MODEL_SUCCESS,
} from '../../actions/app/index.tsx';
import { SET_CAN_USER_INVITE } from '../../actions/can-user-invite/index.tsx';
import { EXTENSION_FETCH_SUCCESS } from '../../actions/extension/index.tsx';
import { FETCH_PERMISSIONS_SUCCESS } from '../../actions/permissions/index.tsx';
import {
	CONFLUENCE_ACCESS_ESTABLISHED,
	HAS_CONFLUENCE_SPACES,
	HAS_XFLOW_FROM_WHITEBOARDS_TOUCHPOINT,
	SET_WHITEBOARD_ELIGIBILITY,
	SET_USER_PROPERTY_KEYS,
	SET_USER_PROPERTY_KEY,
} from '../../actions/whiteboards-entrypoint-experiment/index.tsx';
import type { Action } from '../../types.tsx';
import extensionReducer from './extension/reducer.tsx';
import type { ConfigState } from './types.tsx';

const defaultState: ConfigState = {
	locale: defaultLocale,
	baseUrl: '',
	rapidViewId: '',
	isCompanyManaged: false,
	projectKey: '',
	projectId: 1,
	projectName: '',
	rankCustomFieldId: 0,
	// @ts-expect-error(PARTIAL_RECORD) TS2739 - Type '{}' is missing the following properties from type 'Permissions': CAN_CREATE_ISSUES, CAN_DELETE_ISSUES, CAN_MANAGE_SPRINTS, CAN_EDIT_ISSUES, CAN_EDIT_BOARD_CONFIG
	permissions: {},
	timeTrackingOptions: defaultTimeTrackingOptions,
	cloudId: '',
	atlassianAccountId: null,
	xsrfToken: '',
	backlogStrategy: 'NONE',
	name: '',
	sprintFieldId: null,
	storyPointFieldId: null,
	epicIssueType: null,
	epicLinkField: null,
	jqlQuery: null,
	isInlineCardCreateEnabled: false,
	requestColumnMigration: false,
	extension: {
		operations: [],
	},
	allFeatures: [],
	version: {
		isVersionsFeatureEnabled: false,
	},
	sessionId: uuid(),
	kanbanBoardColumnId: null,
	kanbanBacklogColumnId: null,
	isOrderedByRank: true,
	showEpicAsPanel: false,
	canInviteOthersToProject: false,
	whiteboardsEntrypointExperimentConfig: {
		hasConfluenceAccess: false,
		hasConfluenceSpaces: false,
		hasXFlowFromWhiteboardsTouchpoint: false,
		eligibility: {
			flow: null,
			cohort: 'not-enrolled',
		},
		userPropertyKeys: null,
	},
};

export default function configReducer(
	state: ConfigState = defaultState,
	action: Action,
): ConfigState {
	switch (action.type) {
		case INITIALIZE_APP_PROPS:
			return {
				...state,
				...action.payload,
				sessionId: uuid(),
			};

		case LOAD_CONFIG_DATA_MODEL_SUCCESS: {
			return merge(merge(state, action.payload.config), {
				permissions: state.permissions,
			});
		}

		case SET_APP_DATA:
		case SET_CONFIG_DATA:
			return merge(merge(state, action.payload.config), {
				permissions: merge(state.permissions, action.payload.config.permissions),
			});

		case FETCH_PERMISSIONS_SUCCESS:
			return merge(state, {
				permissions: merge(state.permissions, action.payload.permissions),
			});

		case EXTENSION_FETCH_SUCCESS:
			return chain(state).set('extension', extensionReducer(state.extension, action)).value();

		case SET_CAN_USER_INVITE:
			return merge(state, {
				canInviteOthersToProject: action.payload.canInviteOthersToProject,
			});

		case CONFLUENCE_ACCESS_ESTABLISHED:
			return merge(state, {
				whiteboardsEntrypointExperimentConfig: {
					...state.whiteboardsEntrypointExperimentConfig,
					hasConfluenceAccess: action.payload.hasConfluenceAccess,
				},
			});

		case HAS_CONFLUENCE_SPACES:
			return merge(state, {
				whiteboardsEntrypointExperimentConfig: {
					...state.whiteboardsEntrypointExperimentConfig,
					hasConfluenceSpaces: action.payload.hasConfluenceSpaces,
				},
			});

		case SET_WHITEBOARD_ELIGIBILITY:
			return merge(state, {
				whiteboardsEntrypointExperimentConfig: {
					...state.whiteboardsEntrypointExperimentConfig,
					eligibility: {
						flow: action.payload.flow,
						cohort: action.payload.cohort,
					},
				},
			});

		case HAS_XFLOW_FROM_WHITEBOARDS_TOUCHPOINT:
			return merge(state, {
				whiteboardsEntrypointExperimentConfig: {
					...state.whiteboardsEntrypointExperimentConfig,
					hasXFlowFromWhiteboardsTouchpoint: action.payload.hasXflowFromWhiteboardsTouchpoint,
				},
			});

		case SET_USER_PROPERTY_KEYS:
			return merge(state, {
				whiteboardsEntrypointExperimentConfig: {
					...state.whiteboardsEntrypointExperimentConfig,
					userPropertyKeys: action.payload,
				},
			});

		case SET_USER_PROPERTY_KEY: {
			const userPropertyKeys = state.whiteboardsEntrypointExperimentConfig.userPropertyKeys;
			const userPropertyKeysSet = new Set(userPropertyKeys);
			userPropertyKeysSet.add(action.payload);

			return merge(state, {
				whiteboardsEntrypointExperimentConfig: {
					...state.whiteboardsEntrypointExperimentConfig,
					userPropertyKeys: Array.from(userPropertyKeysSet),
				},
			});
		}

		default:
			return state;
	}
}
